/**
 * OUR SOLUTION（執着の3要素）scroll-square-orbit のフル移植。
 * セクションをピン留め（.solution__sticky）し、スクロール予算内の進行度0→1で4フェーズ進行：
 *   0.00–0.12 Phase1：2枚の四角が対角から弧で登場（fade in / blur→0 / 微回転）
 *   0.00–0.15         見出し「OUR SOLUTION」が上へフェードアウト
 *   0.12–0.22 Phase2：中央で完全集結 → 接触でピンク点灯
 *   0.22–0.92 Phase3：塊のままゆっくり回転 ＋ 左右テキスト(PRIMAL/VICTORY)が順にフェード
 *   0.92–1.00 Phase4：2枚が再分離して対角に飛散（fade out / blur）
 * 進行度は service-stack と同じ「ページスクロール＋ピン」方式で算出（内部スクロールコンテナは使わない）。
 * 参照: addict_intraction / src/demos/scroll-square-orbit.astro
 * 対象: [data-orbit]（.solution）
 */

import type { PageModule } from "./page-module"

const clamp = (v: number, min: number, max: number): number => Math.max(min, Math.min(max, v))
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t

/** power3.out: 1-(1-t)^3 */
const easeOut = (t: number): number => 1 - Math.pow(1 - t, 3)

/** cubic-bezier(0.65,0.05,0.35,0.95) 近似のS字（フェード用） */
const easeInOutS = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

/** solution「一瞬止まる」ホールド。各ホールドが消費する生progress幅（一瞬）＋止める位置(PC/SPで別) */
const HOLD_W = 0.08
const HOLD_PC = [0.32, 0.69] // PC: 01(画像1中央+card01)/02(画像2中央+card02)が決まる進行度
const HOLD_SP = [0.47, 0.76] // SP: 01(画像0定位置+text01)/02(画像1定位置+text02)が決まる進行度

/**
 * 「一瞬止まる」：生スクロール進行度を、指定アニメ進行度(holds)で平坦(ホールド)にした進行度へ変換する。
 * ホールド帯では構図が動かず＝スクロールしても静止して見える（実スクロールは止めないのでSP/PCともカクつかない）。
 * @param {number} raw 生の進行度0-1
 * @param {number[]} holds 止めるアニメ進行度（昇順）
 * @returns {number} ホールド込みのアニメ進行度0-1
 */
const applyHolds = (raw: number, holds: number[]): number => {
  const k = 1 - holds.length * HOLD_W // 移動区間の raw/animP
  let rawCursor = 0
  let prevAnim = 0
  for (const h of holds) {
    const movingRaw = (h - prevAnim) * k
    if (raw < rawCursor + movingRaw) return prevAnim + (raw - rawCursor) / k
    rawCursor += movingRaw
    if (raw < rawCursor + HOLD_W) return h // ホールド中＝構図静止
    rawCursor += HOLD_W
    prevAnim = h
  }
  return prevAnim + (raw - rawCursor) / k
}

/** グループ内の四角のサイズ比・回転オフセット（各グループ1枚構成） */
const SIZE_RATIOS = [1.0]
const ROT_OFFSETS = [0]
/** グループの境界（i < HALF が グループA） */
const HALF = 1

/** PC背景画像の最大不透明度（1＝ピークで完全不透明） */
const BG_MAX_OPACITY = 1

/** SP：decoの退場開始progress。他要素の退場(0.85)より遅らせ、decoが最後に残って白化＋左右飛散する（大きいほど遅い） */
const SP_DECO_EXIT_START = 0.9

/** SP（≤1024px）判定 */
const isSP = (): boolean => window.innerWidth <= 1024

/**
 * SP：01画像のY移動率（%）。下→定位置→上へ抜ける。
 * 移動はlinear（イージングなし）＝スクロール量に1:1で追従させ、加減速による「ズレて動く」違和感をなくす
 * @param {number} p 進行度0-1
 * @returns {number} translateY(%)
 */
const spImg0Y = (p: number): number => {
  if (p < 0.2) return 105
  if (p < 0.4) return lerp(105, 0, (p - 0.2) / 0.2)
  if (p < 0.55) return 0
  // 退場は -100（=自身の高さ1枚分）だけ上へ。02が真下から同じ100%で追従するので継ぎ目に隙間が出ない
  if (p < 0.72) return lerp(0, -100, (p - 0.55) / 0.17)
  return -100
}

/**
 * SP：02画像のY移動率（%）。後半に下から定位置へ。移動はlinear（spImg0Yと同様）。
 * 開始は100（=ステージ真下でぴったり接する位置）。01の退場と同じ100%移動で継ぎ目に隙間を作らない
 * @param {number} p 進行度0-1
 * @returns {number} translateY(%)
 */
const spImg1Y = (p: number): number => {
  if (p < 0.55) return 100
  if (p < 0.72) return lerp(100, 0, (p - 0.55) / 0.17)
  return 0
}

/**
 * SP：テキストカードの不透明度（0=01は前半、1=02は後半）
 * @param {number} idx カードindex
 * @param {number} p 進行度0-1
 * @returns {number} 不透明度
 */
const calcSpTextOpacity = (idx: number, p: number): number => {
  if (idx === 0) {
    // 01画像のせり上がり(0.2-0.4)に同期して出現、01画像の退場(0.55-)で消える
    if (p < 0.22) return 0
    if (p < 0.36) return easeInOutS((p - 0.22) / 0.14)
    if (p < 0.56) return 1
    if (p < 0.64) return 1 - easeInOutS((p - 0.56) / 0.08)
    return 0
  }
  // 02画像のせり上がり(0.55-0.72)に同期して出現
  if (p < 0.6) return 0
  if (p < 0.74) return easeInOutS((p - 0.6) / 0.14)
  return 1
}

/**
 * テキストブロックの不透明度（idx=0は先に、idx=1は後に登場し、両方66-72%で退場）
 * @param {number} idx - テキストブロックのindex
 * @param {number} progress - 進行度0-1
 * @returns {number} 不透明度
 */
const calcTextOpacity = (idx: number, progress: number): number => {
  const ranges = [
    // 01(--left)は02が出始める0.46で退場開始＝02とクロスフェードで消える
    { start: 0.18, enterEnd: 0.24, exitStart: 0.46, end: 0.52 },
    { start: 0.46, enterEnd: 0.52, exitStart: 0.86, end: 0.92 },
  ]
  const r = ranges[idx]
  if (!r || progress < r.start || progress >= r.end) return 0
  if (progress < r.enterEnd) return easeInOutS((progress - r.start) / (r.enterEnd - r.start))
  if (progress > r.exitStart) return 1 - easeInOutS((progress - r.exitStart) / (r.end - r.exitStart))
  return 1
}

/**
 * PC背景画像のスライド量と不透明度（下→上へ連続移動しつつ入退場）。見本 scroll-square-orbit 準拠。
 * 01(idx0)は18-46%、02(idx1)は46-72%で登場→退場。translateYは +100%(下) → -100%(上) を通過。
 * @param {number} idx - 画像index（0/1）
 * @param {number} progress - 進行度0-1
 * @returns {{opacity:number, y:number}} 不透明度(0-1)とtranslateY(%)
 */
const calcBgImage = (idx: number, progress: number): { opacity: number; y: number } => {
  const ranges = [
    { start: 0.18, enterEnd: 0.24, exitStart: 0.4, end: 0.46 },
    { start: 0.46, enterEnd: 0.52, exitStart: 0.86, end: 0.92 },
  ]
  const r = ranges[idx]
  if (!r || progress < r.start || progress >= r.end) {
    return { opacity: 0, y: !r || progress < r.start ? 100 : -100 }
  }
  const tFull = (progress - r.start) / (r.end - r.start)
  const y = 100 - 200 * tFull
  let opacity = 1
  if (progress < r.enterEnd) {
    opacity = easeInOutS((progress - r.start) / (r.enterEnd - r.start))
  } else if (progress > r.exitStart) {
    opacity = 1 - easeInOutS((progress - r.exitStart) / (r.end - r.exitStart))
  }
  return { opacity, y }
}

/**
 * 初期化。reduced-motion では静的な集結状態＋テキスト表示で固定。
 * 現在ページの <main>(scope) を受け取り mount し、リスナー/rAF を解放する destroy を返す。
 * @param {HTMLElement} scope 現在ページの <main>
 * @returns {(() => void) | void} destroy 関数（対象が無い or reduced-motion では void）
 */
export const mountSolutionOrbit: PageModule = (scope) => {
  const section = scope.querySelector<HTMLElement>("[data-orbit]")
  if (!section) return
  const sticky = section.querySelector<HTMLElement>(".solution__sticky")
  const squares = Array.from(section.querySelectorAll<HTMLElement>(".solution__deco span"))
  const textBlocks = Array.from(section.querySelectorAll<HTMLElement>("[data-orbit-text]"))
  const heading = section.querySelector<HTMLElement>("[data-orbit-heading]")
  const spStage = section.querySelector<HTMLElement>(".solution__spStage")
  const spImgs = Array.from(section.querySelectorAll<HTMLElement>("[data-sp-img]"))
  const bgImgs = Array.from(section.querySelectorAll<HTMLElement>("[data-bg-img]"))
  if (!sticky || squares.length === 0) return

  // reduced-motion：静的な集結状態（SPは01画像＋01テキスト、PCは集結＋全テキスト）
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    const W = sticky.clientWidth
    const H = sticky.clientHeight
    if (isSP()) {
      const cx = spStage ? spStage.offsetLeft + spStage.offsetWidth / 2 : W / 2
      const cy = spStage ? spStage.offsetTop + spStage.offsetHeight / 2 : H * 0.42
      const size = W * 0.86
      squares.forEach((sq, i) => {
        sq.style.width = `${size}px`
        sq.style.transform = `translate3d(${cx}px, ${cy}px, 0) translate(-50%, -50%) rotate(${i === 0 ? -20 : 90}deg)`
        sq.style.opacity = "1"
      })
      if (spImgs[0]) spImgs[0].style.transform = "translateY(0%)"
      if (spImgs[1]) spImgs[1].style.transform = "translateY(105%)"
      textBlocks.forEach((tb, idx) => (tb.style.opacity = idx === 0 ? "1" : "0"))
    } else {
      const size = W * 0.5
      squares.forEach((sq, i) => {
        sq.style.width = `${size}px`
        sq.style.transform = `translate3d(${W / 2}px, ${H / 2}px, 0) translate(-50%, -50%) rotate(${i === 0 ? -20 : 20}deg)`
        sq.style.opacity = "1"
      })
      textBlocks.forEach((tb) => (tb.style.opacity = "1"))
    }
    if (heading) heading.style.opacity = "1"
    return
  }

  let rafId: number | null = null
  const ac = new AbortController()

  /**
   * SP：四角スピン → 01画像せり上がり＋01テキスト → スクロールで01が上へ・02がせり上がり＋02テキスト
   */
  const updateSP = (): void => {
    const rect = section.getBoundingClientRect()
    const W = sticky.clientWidth
    const H = sticky.clientHeight
    const budget = Math.max(section.offsetHeight - H, 1)
    const progress = applyHolds(clamp(-rect.top / budget, 0, 1), HOLD_SP) // 01/02が決まる瞬間に一瞬ホールド
    // 画像・テキスト・見出しの退場フェード。decoが飛ぶ前(SP_DECO_EXIT_START)に消え切るよう、その0.1手前→SP_DECO_EXIT_STARTで0にする
    const exitFade = 1 - clamp((progress - (SP_DECO_EXIT_START - 0.1)) / 0.1, 0, 1)

    // ステージ中心（sticky基準）に四角を集めてスピン
    const cx = spStage ? spStage.offsetLeft + spStage.offsetWidth / 2 : W / 2
    const cy = spStage ? spStage.offsetTop + spStage.offsetHeight / 2 : H * 0.42
    const sqSize = W * 0.86
    const sqOpacity = clamp(progress / 0.1, 0, 1)
    // decoは他要素(exitFade 0.85–1.0)より遅らせて退場（SP_DECO_EXIT_START→1.0で白化＋左右飛散）
    const decoExitT = clamp((progress - SP_DECO_EXIT_START) / (1 - SP_DECO_EXIT_START), 0, 1)
    squares.forEach((sq, i) => {
      const baseRot = i === 0 ? -20 : 90
      const spin = progress * 320 * (i === 0 ? 1 : -1)
      // 末尾はPC同様に左右へ飛散（i=0→右 / i=1→左）＋ブラー＋フェード＋accent解除で「白くなって飛ぶ」
      const flyX = cx + (i === 0 ? 1 : -1) * decoExitT * W * 0.85
      sq.style.width = `${sqSize}px`
      sq.style.transform = `translate3d(${flyX}px, ${cy}px, 0) translate(-50%, -50%) rotate(${baseRot + spin}deg)`
      sq.style.opacity = String(sqOpacity * (1 - decoExitT))
      sq.style.filter = decoExitT > 0 ? `blur(${decoExitT * 5}px)` : "none"
      // 集結中(0.16〜)だけaccent点灯。退場開始(SP_DECO_EXIT_START)で解除→$gray-350へ戻り「白く」なる
      sq.classList.toggle("is-touching", progress > 0.16 && progress < SP_DECO_EXIT_START)
    })

    // 画像：01→02 の上下スライド（末尾でexitFadeで退場）
    if (spImgs[0]) {
      spImgs[0].style.transform = `translateY(${spImg0Y(progress)}%)`
      spImgs[0].style.opacity = String(exitFade)
    }
    if (spImgs[1]) {
      spImgs[1].style.transform = `translateY(${spImg1Y(progress)}%)`
      spImgs[1].style.opacity = String(exitFade)
    }

    // テキスト：01/02 クロスフェード（末尾でexitFade退場）
    textBlocks.forEach((tb, idx) => {
      tb.style.opacity = String(calcSpTextOpacity(idx, progress) * exitFade)
    })

    // 見出し：SPは上部に出しっぱなしだが、末尾(0.85→1.0)でexitFadeで一緒に退場
    if (heading) {
      heading.style.opacity = String(exitFade)
      heading.style.transform = "none"
    }
  }

  const updatePC = (): void => {
    const rect = section.getBoundingClientRect()
    const W = sticky.clientWidth
    const H = sticky.clientHeight
    // ピン予算内の進行度（-rect.top を予算でクランプ）＋ 01/02が決まる瞬間に一瞬ホールド
    const budget = Math.max(section.offsetHeight - H, 1)
    const progress = applyHolds(clamp(-rect.top / budget, 0, 1), HOLD_PC)

    const cx = W / 2
    const cy = H / 2
    const SIZE_BASE = W * 0.5
    const TOUCH_THRESHOLD = SIZE_BASE * 0.6

    // ─── フェーズ別パラメータ ───
    let entryT = 0
    let exitT = 0
    let rotT = 0
    let squareOpacity = 0
    let squareBlur = 0

    if (progress < 0.12) {
      // Phase1：弧で登場
      const te = easeOut(progress / 0.12)
      entryT = te
      squareOpacity = te
      squareBlur = lerp(8, 0, te)
      rotT = te * 0.15
    } else if (progress < 0.22) {
      // Phase2：中央集結 → ピンク化
      entryT = 1
      squareOpacity = 1
      rotT = lerp(0.15, 0.3, (progress - 0.12) / 0.1)
    } else if (progress < 0.92) {
      // Phase3：塊のまま持続（ゆっくり回転＋テキスト切替）。中身をしっかり見せるため0.92まで延長
      entryT = 1
      squareOpacity = 1
      rotT = lerp(0.3, 0.7, (progress - 0.22) / 0.7)
    } else {
      // Phase4：再分離して対角に飛散（退場を末尾0.92–1.0に圧縮＝死に区間をほぼ消す）
      const t = easeInOutS((progress - 0.92) / 0.08)
      entryT = 1
      exitT = t
      rotT = lerp(0.7, 1.0, t)
      squareOpacity = 1 - t
      squareBlur = t * 5
    }

    // ─── グループ中心の動き（弧を描く対角通り抜け） ───
    const startA = { x: -W * 0.3, y: cy + H * 0.5 }
    const endA = { x: W * 1.3, y: cy - H * 0.5 }
    const startB = { x: W * 1.3, y: cy - H * 0.5 }
    const endB = { x: -W * 0.3, y: cy + H * 0.5 }
    const mid = { x: cx, y: cy }
    const arcAmp = H * 0.22

    let gA: { x: number; y: number }
    let gB: { x: number; y: number }
    if (exitT > 0) {
      const t = exitT
      gA = { x: lerp(mid.x, endA.x, t), y: lerp(mid.y, endA.y, t) - arcAmp * Math.sin(t * Math.PI) }
      gB = { x: lerp(mid.x, endB.x, t), y: lerp(mid.y, endB.y, t) + arcAmp * Math.sin(t * Math.PI) }
    } else {
      const t = entryT
      gA = { x: lerp(startA.x, mid.x, t), y: lerp(startA.y, mid.y, t) - arcAmp * Math.sin(t * Math.PI) }
      gB = { x: lerp(startB.x, mid.x, t), y: lerp(startB.y, mid.y, t) + arcAmp * Math.sin(t * Math.PI) }
    }

    // ─── 各四角の位置・サイズ・回転 ───
    const positions = squares.map((_, i) => {
      const isA = i < HALF
      const localIdx = i % HALF
      const gc = isA ? gA : gB
      const size = SIZE_BASE * SIZE_RATIOS[localIdx]
      const groupSign = isA ? 1 : -1
      const rot = ROT_OFFSETS[localIdx] + rotT * 180 * groupSign
      return { x: gc.x, y: gc.y, rot, size }
    })

    // ─── 接触判定（中央集結し始めから） ───
    const touching = new Set<number>()
    if (entryT > 0.3) {
      for (let i = 0; i < positions.length; i++) {
        for (let j = i + 1; j < positions.length; j++) {
          const dx = positions[i].x - positions[j].x
          const dy = positions[i].y - positions[j].y
          if (Math.hypot(dx, dy) < TOUCH_THRESHOLD) {
            touching.add(i)
            touching.add(j)
          }
        }
      }
    }

    // ─── 反映：四角 ───
    squares.forEach((sq, i) => {
      const p = positions[i]
      sq.style.width = `${p.size}px`
      sq.style.transform = `translate3d(${p.x}px, ${p.y}px, 0) translate(-50%, -50%) rotate(${p.rot}deg)`
      sq.style.opacity = String(squareOpacity)
      sq.style.filter = `blur(${squareBlur}px)`
      sq.classList.toggle("is-touching", touching.has(i))
    })

    // ─── 反映：テキストブロック ───
    textBlocks.forEach((tb, idx) => {
      tb.style.opacity = String(calcTextOpacity(idx, progress))
    })

    // ─── 反映：背景画像（四角の背面で下→上へ流れる・うっすら／見本準拠） ───
    bgImgs.forEach((bg, idx) => {
      const s = calcBgImage(idx, progress)
      bg.style.setProperty("--bg-opacity", String(s.opacity * BG_MAX_OPACITY))
      bg.style.setProperty("--bg-y", `${s.y}%`)
    })

    // ─── 反映：見出し（0-15%で上へフェードアウト） ───
    if (heading) {
      const he = easeOut(clamp(progress / 0.15, 0, 1))
      heading.style.opacity = String(1 - he)
      heading.style.transform = `translateY(${-he * 100}%)`
    }
  }

  // 画面幅で SP/PC の駆動を切替
  const update = (): void => {
    if (isSP()) updateSP()
    else updatePC()
  }

  const onScroll = (): void => {
    if (rafId !== null) return
    rafId = requestAnimationFrame(() => {
      rafId = null
      update()
    })
  }

  window.addEventListener("scroll", onScroll, { passive: true, signal: ac.signal })
  window.addEventListener("resize", onScroll, { passive: true, signal: ac.signal })
  update()

  return () => {
    ac.abort()
    if (rafId !== null) cancelAnimationFrame(rafId)
  }
}
